import { useCapability } from "../../app/bandContext.js";
import { SettingsAccountTab } from "./SettingsAccountTab.js";
import { SettingsProfileTab } from "./SettingsProfileTab.js";
import { SettingsFeaturesTab } from "./SettingsFeaturesTab.js";
import { SettingsConnectionsTab } from "./SettingsConnectionsTab.js";
import { SettingsTeamTab } from "./SettingsTeamTab.js";
import { SettingsModalShell } from "./SettingsModalShell.js";
import { SettingsTutorialsTab } from "./tutorials/SettingsTutorialsTab.js";
import { useBandTutorials } from "../../app/hooks/useTutorials.js";
import { visibleSettingsTabs, type SettingsTab } from "./settingsTabs.js";

export interface SettingsModalProps {
  onClose: () => void;
  /** Which tab to open on. Defaults to the first tab this seat can see. */
  initialTab?: SettingsTab;
}

const BODIES: Record<SettingsTab, () => React.ReactElement> = {
  account: SettingsAccountTab,
  profile: SettingsProfileTab,
  features: SettingsFeaturesTab,
  connections: SettingsConnectionsTab,
  team: SettingsTeamTab,
  tutorials: SettingsTutorialsTab,
};

/**
 * Settings as a modal — the normal way in, opened from the topbar gear (ruled 2026‑08‑17).
 *
 * The `/settings/*` ROUTES deliberately survive as a full page: the OAuth connect flow returns providers to
 * `/settings/connections` (`IConnectionOAuthService.DefaultReturnPath`), which arrives as a cold full-page
 * load with no app behind it, and the mobile shell reaches Settings through its own tab. So this is a second
 * *chrome*, never a second implementation — both mount the very same tab components, and the tab list itself
 * lives in `settingsTabs.ts` so the page and the modal can never drift on which tabs exist or who may see them.
 *
 * The dialog chrome (card, tab strip, the one scroll region, and opening on the first tab the seat can SEE)
 * is `SettingsModalShell`, shared with the org Settings modal — the two dialogs are the same dialog over
 * different tab sets.
 */
export function SettingsModal({ onClose, initialTab }: SettingsModalProps) {
  const hasTutorials = (useBandTutorials().data?.length ?? 0) > 0;
  const tabs = visibleSettingsTabs(useCapability("band:manage"), { hasTutorials });
  return (
    <SettingsModalShell
      tabs={tabs}
      initialTab={initialTab}
      bodies={BODIES}
      tabsLabel="Settings sections"
      onClose={onClose}
    />
  );
}
